import React, { useMemo } from "react";

import { orderBy } from "lodash";

import {
  AltAssetId,
  ExternalAccountId,
  FartherAccountId,
} from "@fartherfinance/frontend/api/Types";

import Chip from "@src/sharedComponents/Chip/Chip";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import TableCell from "@src/sharedComponents/Table/TableCell/TableCell";
import { Cell, Row } from "@src/sharedComponents/Table/Types";
import Text from "@src/sharedComponents/Typography/Text";
import toUSD from "@src/utils/toUSD";

import styles from "./ProjectionTable.module.css";

interface AccountProjection {
  accountId: FartherAccountId | ExternalAccountId | AltAssetId;
  accountName: string;
  accountType: string;
  marketValue: number | undefined;
  valueAtRetirement: number | null | undefined;
  valueAtEndOfPlan: number | undefined;
}

const tableKeys = [
  "Account",
  "Type",
  "Current",
  "At Retirement",
  "End of Plan",
] as const;

type TableKey = typeof tableKeys[number];

const tableKeysNoAtRetirement = [
  "Account",
  "Type",
  "Current",
  "End of Plan",
] as const;

type TableKeysNoAtRetirement = typeof tableKeysNoAtRetirement[number];

interface Props {
  projections: AccountProjection[];
}

const FinancialPlansProjectionTable = ({ projections }: Props): JSX.Element => {
  const doesEveryAccountHaveNullAtRetirement = useMemo(
    () => projections.every((a) => a.valueAtRetirement === null),
    [projections]
  );

  const rows: Row<TableKey, Cell>[] = useMemo(() => {
    return orderBy(projections, [(p) => p.accountName], ["desc"]).map((p) => {
      return {
        key: p.accountId,
        Account: {
          value: p.accountName,
        },
        Type: {
          value: (
            <Chip
              label={
                //Capitalize the first letter of the account type
                p.accountType.charAt(0).toUpperCase() + p.accountType.slice(1)
              }
              backgroundColor="var(--surface)"
            />
          ),
          fullValue: p.accountType,
        },
        Current: {
          value:
            p.marketValue === undefined
              ? "--"
              : toUSD(p.marketValue, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }),
        },
        "At Retirement": {
          value:
            p.valueAtRetirement === undefined || p.valueAtRetirement === null
              ? "--"
              : toUSD(p.valueAtRetirement, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }),
        },
        "End of Plan": {
          value:
            p.valueAtEndOfPlan === undefined
              ? "--"
              : toUSD(p.valueAtEndOfPlan, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }),
        },
      };
    });
  }, [projections]);

  const rowsNoAtRetirement: Row<TableKeysNoAtRetirement, Cell>[] = useMemo(
    () =>
      rows.map((row) => {
        const { "At Retirement": _, ...rest } = row; // take out 'At Retirement'
        return rest;
      }),
    [rows]
  );

  if (doesEveryAccountHaveNullAtRetirement) {
    return (
      <div className={styles.container}>
        <TableHeaderAndText />

        <FullDataTable
          defaultRowsPerPage={100}
          hidePaginationKeepFooter={true}
          className={styles.table}
          tableContainerClassName={styles.tableContainer}
          headerClassName={styles.tableHeader}
          headerCellClassName={styles.tableHeaderCell}
          tableRow={styles.tableRow}
          footerClassName={styles.tableFooter}
          footerToolbarClassName={styles.tableFooterToolbar}
          columns={tableKeysNoAtRetirement}
          rows={rowsNoAtRetirement}
          defaultSortColumn={["Account", "asc"]}
          disableColumnSorting={tableKeysNoAtRetirement}
          emptyCell={<></>}
        >
          {projections.length === 0 && (
            <EmptyTable numCols={tableKeysNoAtRetirement.length} />
          )}
        </FullDataTable>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <TableHeaderAndText />

      <FullDataTable
        defaultRowsPerPage={100}
        hidePaginationKeepFooter={true}
        className={styles.table}
        tableContainerClassName={styles.tableContainer}
        headerClassName={styles.tableHeader}
        headerCellClassName={styles.tableHeaderCell}
        tableRow={styles.tableRow}
        footerClassName={styles.tableFooter}
        footerToolbarClassName={styles.tableFooterToolbar}
        columns={tableKeys}
        rows={rows}
        defaultSortColumn={["Account", "asc"]}
        emptyCell={<></>}
      >
        {projections.length === 0 && <EmptyTable numCols={tableKeys.length} />}
      </FullDataTable>
    </div>
  );
};

export default FinancialPlansProjectionTable;

interface EmptyTableProps {
  numCols: number;
}

const EmptyTable: React.FC<EmptyTableProps> = ({ numCols }) => {
  return (
    <TableCell colSpan={numCols}>
      <div className={styles.emptyTable}>
        <div className={styles.emptyTableText}>No projections available</div>
      </div>
    </TableCell>
  );
};

const TableHeaderAndText: React.FC = () => {
  return (
    <>
      <Text variant="heading1">Accounts</Text>

      <Text variant="body" color={"textSubtle"}>
        A table of the projected values of the accounts in this plan
      </Text>
    </>
  );
};
